/** @format */

import {
	AI_POLL_TIMEOUT_REASON,
	IMPORT_PIPELINE_STAGES,
	parseQuestionNumber,
	type ImportAiQuestionState,
	type ImportJob,
	type ImportPipelineStage,
} from "@question-bank/shared";

/** What one step of the stepper shows. */
export type ImportStageStepState = "waiting" | "inProgress" | "done" | "failed";

export interface ImportStageStep {
	stage: ImportPipelineStage;
	state: ImportStageStepState;
	/** Numbers this stage has recorded, in pipeline order. */
	questionNumbers: string[];
	/** Classify questions with their AI platform state, when the job stored ids. */
	trackedQuestions?: ReadonlyArray<{ number: string; aiState: ImportAiQuestionState }>;
	/**
	 * Size of the set when the numbers themselves were not stored, or when a
	 * stage has finished only some of them. Absent when `questionNumbers` is the whole set.
	 */
	questionTotal?: number;
	/** Extract KaTeX finished and stored an empty list. */
	hasNoKatex?: boolean;
}

/**
 * What the job screen draws for the PDF pipeline.
 *
 * `hidden` is a CSV job. `unrecorded` is a PDF job whose stage was never stored
 * and cannot be inferred. `steps` is the pipeline in order. Align bilingual is
 * drawn only when the job stored both Welsh PDF keys.
 */
export type ImportStageVisual =
	| { kind: "hidden" }
	| { kind: "unrecorded" }
	| { kind: "steps"; steps: ImportStageStep[] };

const stageListsQuestion = (
	step: Pick<ImportStageStep, "questionNumbers" | "trackedQuestions">,
	number: string,
): boolean =>
	(step.trackedQuestions?.some((question) => question.number === number) ?? false) ||
	step.questionNumbers.includes(number);

/**
 * True when `number` is on a stage at or before `index` and on a later stage.
 * The connector under `index` then joins those stages. An empty stage in between
 * still counts, so the line continues through Read specification.
 */
export const questionCrossesConnector = (
	steps: ReadonlyArray<Pick<ImportStageStep, "questionNumbers" | "trackedQuestions">>,
	index: number,
	number: string | null,
): boolean => {
	if (!number || index < 0 || index >= steps.length - 1) {
		return false;
	}
	const isBefore = steps.slice(0, index + 1).some((step) => stageListsQuestion(step, number));
	const isAfter = steps.slice(index + 1).some((step) => stageListsQuestion(step, number));
	return isBefore && isAfter;
};

/** One recorded number on a stage, with the AI state when Classify stored one. */
export interface StageQuestionChip {
	number: string;
	aiState?: ImportAiQuestionState;
}

/**
 * One question or part in the stage list.
 *
 * `number` is the full stored number when this node has a chip. A missing parent
 * keeps the reconstructed number and has `chip: null`, so the screen can show
 * the question without making it a pin target. `label` is the token to draw:
 * `1`, `(a)`, `(ch)`, or `(i)`.
 */
export interface StageQuestionNode {
	number: string;
	label: string;
	chip: StageQuestionChip | null;
	/** A number missing between the lowest and highest sibling. */
	isGap: boolean;
	children: StageQuestionNode[];
}

const pathOf = (value: string): { labels: string[]; numbers: string[] } => {
	const parsed = parseQuestionNumber(value);
	if (parsed.root === "") {
		return { labels: [value], numbers: [value] };
	}
	const labels = [parsed.root];
	const numbers = [parsed.root];
	for (const part of parsed.parts) {
		labels.push(`(${part})`);
		numbers.push(`${numbers[numbers.length - 1]}(${part})`);
	}
	return { labels, numbers };
};

/**
 * Group stage numbers for the stepper.
 *
 * A bare number is a question. Each `(...)` token is a part under the previous
 * token. A letter and a Roman numeral are both parts. `1(i)` sits under `1`.
 * `1(a)(i)` sits under `1(a)`. Order follows the list. The first chip for a
 * number wins.
 */
export const groupStageQuestions = (chips: readonly StageQuestionChip[]): StageQuestionNode[] => {
	const roots: StageQuestionNode[] = [];
	const byNumber = new Map<string, StageQuestionNode>();
	const ensure = (number: string, label: string, parent: StageQuestionNode | null): StageQuestionNode => {
		const found = byNumber.get(number);
		if (found) {
			return found;
		}
		const node: StageQuestionNode = { number, label, chip: null, isGap: false, children: [] };
		byNumber.set(number, node);
		if (parent) {
			parent.children.push(node);
		} else {
			roots.push(node);
		}
		return node;
	};

	for (const chip of chips) {
		const path = pathOf(chip.number);
		let parent: StageQuestionNode | null = null;
		let node: StageQuestionNode | null = null;
		for (let index = 0; index < path.numbers.length; index += 1) {
			const number = path.numbers[index];
			const label = path.labels[index];
			if (number === undefined || label === undefined) {
				continue;
			}
			node = ensure(number, label, parent);
			parent = node;
		}
		if (node && node.chip === null) {
			node.chip = chip;
			node.number = chip.number;
		}
	}
	return roots;
};

const WELSH_LETTERS = [
	"a",
	"b",
	"c",
	"ch",
	"d",
	"dd",
	"e",
	"f",
	"ff",
	"g",
	"ng",
	"h",
	"i",
	"l",
	"ll",
	"m",
	"n",
	"o",
	"p",
	"ph",
	"r",
	"rh",
	"s",
	"t",
	"th",
	"u",
	"w",
	"y",
] as const;

const WELSH_LETTER_SET = new Set<string>(WELSH_LETTERS);
const WELSH_DIGRAPHS = new Set<string>(["ch", "dd", "ff", "ng", "ll", "ph", "rh", "th"]);

const ROMAN_VALUES: Array<[number, string]> = [
	[100, "c"],
	[90, "xc"],
	[50, "l"],
	[40, "xl"],
	[10, "x"],
	[9, "ix"],
	[5, "v"],
	[4, "iv"],
	[1, "i"],
];

const isRomanToken = (token: string): boolean => /^[ivxlcdm]+$/i.test(token);

const romanToInt = (roman: string): number | null => {
	const values: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
	let total = 0;
	let previous = 0;
	for (let index = roman.length - 1; index >= 0; index -= 1) {
		const current = values[roman[index]?.toLowerCase() ?? ""];
		if (current === undefined) {
			return null;
		}
		total += current < previous ? -current : current;
		previous = current;
	}
	return total;
};

const intToRoman = (value: number): string => {
	let rest = value;
	let roman = "";
	for (const [amount, glyph] of ROMAN_VALUES) {
		while (rest >= amount) {
			roman += glyph;
			rest -= amount;
		}
	}
	return roman;
};

type SiblingSequence = "number" | "roman" | "welsh" | "letter";

/** Sequence shared by every sibling. Mixed or unknown tokens have no gaps. */
const siblingSequence = (tokens: readonly string[]): SiblingSequence | null => {
	if (tokens.length === 0) {
		return null;
	}
	if (tokens.every((token) => /^\d+$/.test(token))) {
		return "number";
	}
	const hasWelshDigraph = tokens.some((token) => WELSH_DIGRAPHS.has(token.toLowerCase()));
	if (hasWelshDigraph) {
		return tokens.every((token) => WELSH_LETTER_SET.has(token.toLowerCase())) ? "welsh" : null;
	}
	const hasRomanRun = tokens.every((token) => isRomanToken(token)) && tokens.some((token) => token.length > 1);
	if (hasRomanRun) {
		return "roman";
	}
	if (tokens.every((token) => /^[a-z]$/i.test(token))) {
		return "letter";
	}
	return null;
};

/** Tokens from the lowest sibling to the highest. Nothing is added before the first. */
const expectedTokens = (sequence: SiblingSequence, tokens: readonly string[]): string[] => {
	if (sequence === "number") {
		const values = tokens.map((token) => Number(token));
		const lowest = Math.min(...values);
		const highest = Math.max(...values);
		return Array.from({ length: highest - lowest + 1 }, (_, index) => String(lowest + index));
	}
	if (sequence === "roman") {
		const values = tokens.map((token) => romanToInt(token) ?? 0);
		const lowest = Math.min(...values);
		const highest = Math.max(...values);
		return Array.from({ length: highest - lowest + 1 }, (_, index) => intToRoman(lowest + index));
	}
	const alphabet: readonly string[] = sequence === "welsh" ? WELSH_LETTERS : "abcdefghijklmnopqrstuvwxyz".split("");
	const indexes = tokens.map((token) => alphabet.indexOf(token.toLowerCase()));
	const lowest = Math.min(...indexes);
	const highest = Math.max(...indexes);
	return alphabet.slice(lowest, highest + 1);
};

const tokenOf = (node: StageQuestionNode): string =>
	node.label.startsWith("(") && node.label.endsWith(")") ? node.label.slice(1, -1) : node.label;

const gapNode = (token: string, parentNumber: string | null): StageQuestionNode => {
	const number = parentNumber === null ? token : `${parentNumber}(${token})`;
	const label = parentNumber === null ? token : `(${token})`;
	return { number, label, chip: null, isGap: true, children: [] };
};

const orderWithGaps = (nodes: readonly StageQuestionNode[], parentNumber: string | null): StageQuestionNode[] => {
	const tokens = nodes.map((node) => tokenOf(node));
	const sequence = siblingSequence(tokens);
	if (!sequence) {
		return [...nodes];
	}
	const expected = expectedTokens(sequence, tokens);
	if (expected.length === nodes.length) {
		return [...nodes];
	}
	const byToken = new Map<string, StageQuestionNode[]>();
	for (const node of nodes) {
		const key = tokenOf(node).toLowerCase();
		const list = byToken.get(key) ?? [];
		list.push(node);
		byToken.set(key, list);
	}
	const placed: StageQuestionNode[] = [];
	for (const token of expected) {
		const found = byToken.get(token.toLowerCase());
		const next = found?.shift();
		placed.push(next ?? gapNode(token, parentNumber));
	}
	for (const rest of byToken.values()) {
		placed.push(...rest);
	}
	return placed;
};

const fillSiblingGaps = (nodes: readonly StageQuestionNode[], parentNumber: string | null): StageQuestionNode[] =>
	orderWithGaps(nodes, parentNumber).map((node) =>
		node.isGap ? node : { ...node, children: fillSiblingGaps(node.children, node.number) },
	);

/**
 * Insert holes between the lowest and highest sibling.
 *
 * Question numbers, letter parts, Roman parts, and Welsh parts each keep
 * their own sequence. A stage that stored no numbers has nothing to fill.
 */
export const withQuestionGaps = (nodes: readonly StageQuestionNode[]): StageQuestionNode[] =>
	fillSiblingGaps(nodes, null);

/** Gap nodes in tree order, including a missing part under a received question. */
export const flattenQuestionGaps = (nodes: readonly StageQuestionNode[]): StageQuestionNode[] =>
	nodes.flatMap((node) => [...(node.isGap ? [node] : []), ...flattenQuestionGaps(node.children)]);

/**
 * Question numbers to fade in.
 *
 * The first draw seeds `seen` and arrives none, so a full list does not animate
 * on load. A later draw arrives only numbers that were absent.
 */
export const arrivingQuestionNumbers = (
	seen: ReadonlySet<string>,
	numbers: readonly string[],
	hasSeeded: boolean,
): { arriving: ReadonlySet<string>; seen: Set<string> } => {
	if (!hasSeeded) {
		return { arriving: new Set(), seen: new Set(numbers) };
	}
	const arriving = new Set<string>();
	const next = new Set(seen);
	for (const number of numbers) {
		if (!next.has(number)) {
			arriving.add(number);
			next.add(number);
		}
	}
	return { arriving, seen: next };
};

const hasWelshPdfKeys = (job: ImportJob): boolean => Boolean(job.sourceKeys?.paperCy && job.sourceKeys?.markSchemeCy);

const stagesFor = (job: ImportJob): readonly ImportPipelineStage[] =>
	hasWelshPdfKeys(job) ? IMPORT_PIPELINE_STAGES : IMPORT_PIPELINE_STAGES.filter((stage) => stage !== "AlignBilingual");

const bareSteps = (
	stages: readonly ImportPipelineStage[],
	state: ImportStageStepState,
): Array<Pick<ImportStageStep, "stage" | "state">> => stages.map((stage) => ({ stage, state }));

/** Stages before `index` are done. The stage at `index` has `current`. The rest are waiting. */
const bareStepsUpTo = (
	stages: readonly ImportPipelineStage[],
	index: number,
	current: ImportStageStepState,
): Array<Pick<ImportStageStep, "stage" | "state">> =>
	stages.map((stage, stageIndex) => ({
		stage,
		state: stageIndex < index ? "done" : stageIndex === index ? current : "waiting",
	}));

/**
 * Question numbers to draw on one stage.
 *
 * A waiting stage shows none. Extract paper shows the numbers stored for that
 * stage. Extract mark scheme shows the items stored for that stage. A later
 * stage does not replace either list. A stage that stored numbers shows those,
 * and a total when it has not finished the aligned set. A job recorded before
 * numbers were stored shows the extracted count on Align and Classify only.
 */
const questionProgress = (
	job: ImportJob,
	stage: ImportPipelineStage,
	state: ImportStageStepState,
): Pick<ImportStageStep, "questionNumbers" | "questionTotal" | "trackedQuestions" | "hasNoKatex"> => {
	if (state === "waiting" || stage === "ParseSpecification") {
		return { questionNumbers: [] };
	}
	if (stage === "Classify" && job.aiQuestions && Object.keys(job.aiQuestions).length > 0) {
		return classifyTracking(job);
	}
	const numbers = job.stageQuestionNumbers?.[stage] ?? [];
	const aligned = job.stageQuestionNumbers?.Align?.length ?? 0;
	if (numbers.length > 0) {
		const total = stage === "Classify" && aligned > numbers.length ? aligned : undefined;
		return { questionNumbers: numbers, ...(total === undefined ? {} : { questionTotal: total }) };
	}
	if (stage === "ExtractKatex" && state === "done" && job.stageQuestionNumbers?.ExtractKatex?.length === 0) {
		return { questionNumbers: [], hasNoKatex: true };
	}
	const extracted = job.counts?.extracted ?? 0;
	if ((stage === "Align" || stage === "Classify") && extracted > 0) {
		return { questionNumbers: [], questionTotal: extracted };
	}
	if (stage === "WriteDrafts" && state === "done" && job.createdQuestionIds.length > 0) {
		return { questionNumbers: [], questionTotal: job.createdQuestionIds.length };
	}
	return { questionNumbers: [] };
};

/** Question numbers in align order, each with the AI state Classify has stored. */
const classifyTracking = (
	job: ImportJob,
): Pick<ImportStageStep, "questionNumbers" | "questionTotal" | "trackedQuestions"> => {
	const stored = job.aiQuestions ?? {};
	const order = job.stageQuestionNumbers?.Align ?? Object.keys(stored);
	const seen = new Set<string>();
	const trackedQuestions = order.map((number) => {
		seen.add(number);
		return { number, aiState: stored[number]?.state ?? "pending" };
	});
	for (const [number, question] of Object.entries(stored)) {
		if (!seen.has(number)) {
			trackedQuestions.push({ number, aiState: question.state });
		}
	}
	const settled = trackedQuestions
		.filter((question) => question.aiState !== "pending")
		.map((question) => question.number);
	const questionTotal = settled.length < trackedQuestions.length ? trackedQuestions.length : undefined;
	return {
		questionNumbers: settled,
		trackedQuestions,
		...(questionTotal === undefined ? {} : { questionTotal }),
	};
};

const withQuestions = (job: ImportJob, steps: Array<Pick<ImportStageStep, "stage" | "state">>): ImportStageStep[] =>
	steps.map((step) => ({ ...step, ...questionProgress(job, step.stage, step.state) }));

/**
 * Derive the stepper from a job.
 *
 * A finished job, including one being rolled back, shows every stage done.
 * A pending job shows every stage waiting. A running or failed job uses
 * `pipelineStage`. A cancelled job stops at the stored stage without marking it
 * failed. A failed job with no stored stage still shows Classify as failed when
 * the reason is the AI poll timeout and questions were extracted.
 */
export const importStageVisual = (job: ImportJob): ImportStageVisual => {
	if ((job.source ?? "csv") !== "pdf") {
		return { kind: "hidden" };
	}
	const stages = stagesFor(job);
	if (job.status === "pending") {
		return { kind: "steps", steps: withQuestions(job, bareSteps(stages, "waiting")) };
	}
	if (job.status === "complete" || job.status === "rolling_back" || job.status === "rolled_back") {
		return { kind: "steps", steps: withQuestions(job, bareSteps(stages, "done")) };
	}

	const stageIndex = job.pipelineStage ? stages.indexOf(job.pipelineStage) : -1;
	if (job.status === "running") {
		return stageIndex < 0
			? { kind: "unrecorded" }
			: { kind: "steps", steps: withQuestions(job, bareStepsUpTo(stages, stageIndex, "inProgress")) };
	}
	if (job.status === "cancelled") {
		return stageIndex < 0
			? { kind: "unrecorded" }
			: { kind: "steps", steps: withQuestions(job, bareStepsUpTo(stages, stageIndex, "waiting")) };
	}
	if (job.status === "failed") {
		if (stageIndex >= 0) {
			return { kind: "steps", steps: withQuestions(job, bareStepsUpTo(stages, stageIndex, "failed")) };
		}
		const extracted = job.counts?.extracted ?? 0;
		if (extracted > 0 && job.failedReason === AI_POLL_TIMEOUT_REASON) {
			return {
				kind: "steps",
				steps: withQuestions(job, bareStepsUpTo(stages, stages.indexOf("Classify"), "failed")),
			};
		}
		return { kind: "unrecorded" };
	}
	return { kind: "unrecorded" };
};
